import React, { memo } from 'react'
import moment from 'moment'
import { Button } from 'transfix-ui/components/Button'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { Box, Link, makeStyles, Typography } from 'transfix-ui/core'
import { IShipmentIssueStates } from 'types/graphqlTypes'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { useGetShipmentIssues } from 'apps/marketplace/apiTypes/shipmentIssues'
import SLADueStatus from 'apps/marketplace/pages/issues/shared/SLADueStatus/SLADueStatus'
import { OpenInNewTabIcon } from 'transfix-icons'
import { AllowedTimezones } from 'constants/AllowedTimezones'
import { DISPLAY } from 'constants/display'
import { ISSUE_STATUS_LABEL } from 'constants/issueStatus'

interface IShipmentIssuesListProps {
  shipmentId: number
}

const useShipmentIssuesListStyles = makeStyles({
  issueItem: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: '4px',
  },
  sla: {
    margin: '0 4px',
    '& div': {
      width: 'fit-content',
    },
    '& p': {
      fontSize: '14px',
    },
  },
})

const ShipmentIssuesList = memo<IShipmentIssuesListProps>(({ shipmentId }) => {
  const { data } = useGetShipmentIssues({
    shipmentId,
  })

  const classes = useShipmentIssuesListStyles()

  const issuesList = data?.shipmentIssues

  const clickOpenNewIssue = () => {
    window.open(`${__ROOT__}/shipment-details/${shipmentId}?newIssueModal=1`, '_blank')
  }

  const clickIssueDetail = (issueId: number) => {
    window.open(`${__ROOT__}/shipment-details/${shipmentId}?issueId=${issueId}`, '_blank')
  }

  const issueSLAsAndEscalationEnabled = useFeatureFlag('issueSLAsAndEscalation')

  return (
    <Box display='flex' flexDirection='column' mb={2}>
      <Box position='absolute' top={10} right={0} zIndex={2}>
        <Button color='primary' onClick={clickOpenNewIssue}>
          <Box display='flex' alignItems='center'>
            <OpenInNewTabIcon color='white' className='db' />
            <Box ml={1}>Open New Issue</Box>
          </Box>
        </Button>
      </Box>
      <Box>
        <div>Issues</div>
        <ul>
          {issuesList?.map(issue => (
            <li key={issue.id} className={classes.issueItem}>
              <Typography variant='body2'>
                <Link onClick={() => clickIssueDetail(issue.id)}>
                  Issue_{shipmentId}-{issue.id}
                </Link>
                :
              </Typography>
              <Typography variant='body2'>{issue.shipmentIssueType.name}</Typography>
              <Typography variant='body2'>
                {ISSUE_STATUS_LABEL[issue.state as keyof typeof ISSUE_STATUS_LABEL]}{' '}
                {issue.priority ? `- P${issue.priority} ` : `${DISPLAY.empty} `}
              </Typography>
              {issueSLAsAndEscalationEnabled &&
                !!issue.dueAt &&
                issue.state !== IShipmentIssueStates.resolved && (
                  <SLADueStatus
                    iconSize='large'
                    priority={issue.priority}
                    dueAt={issue.dueAt}
                    issueStatus={issue.state as IShipmentIssueStates}
                    className={classes.sla}
                  />
                )}
              <span className='text-caption text-secondary'>
                Last updated:{' '}
                <FormattedDateTime
                  datetime={issue.updatedAt}
                  displayTimezone={moment.tz.guess() as AllowedTimezones}
                  format='timeDateAndYear'
                  inSentence
                />{' '}
                by {issue.updatedBy.firstName} {issue.updatedBy.lastName}
              </span>
            </li>
          ))}
          {!issuesList?.length && '--'}
        </ul>
      </Box>
    </Box>
  )
})

export default ShipmentIssuesList
